"use client";

/**
 * 目前登入者資訊（打 GET /api/v1/me）。
 * 對應任務指示 4：「登入後顯示使用者名與角色」。
 */
import { useEffect, useState } from "react";
import type { CurrentUser } from "@ccheng/ui";
import { ApiError, authApi, getToken, type MeResponse } from "./apiClient";

export interface UseCurrentUserResult {
  /** 給 AppShell/Topbar 用的精簡格式（name + role）。 */
  currentUser: CurrentUser | undefined;
  /** /me 完整回應（角色、權限閉包），需要細節時用這個。 */
  me: MeResponse | undefined;
  loading: boolean;
  error: string | undefined;
}

function toDisplayRole(me: MeResponse): string | undefined {
  if (me.isSuperAdministrator) return "Super Administrator";
  return me.roles.length > 0 ? me.roles.join("、") : undefined;
}

export function useCurrentUser(): UseCurrentUserResult {
  const [me, setMe] = useState<MeResponse | undefined>(undefined);
  // 初始值直接由是否有 token 決定（render 期間算出，非 effect 內 setState）：
  // 沒 token 就沒有東西好讀，loading 一開始即為 false。
  const [loading, setLoading] = useState(() => !!getToken());
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    if (!getToken()) {
      return;
    }

    authApi
      .me()
      .then((res) => {
        if (!cancelled) setMe(res);
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 已由 @ccheng/api-client 的 onUnauthorized 導回 /login；這裡只記錄訊息供畫面顯示。
        setError(err instanceof ApiError ? err.message : "無法取得使用者資訊");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const currentUser: CurrentUser | undefined = me
    ? { name: me.username, role: toDisplayRole(me) }
    : undefined;

  return { currentUser, me, loading, error };
}
